import { SimpleGrid, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useGrowthStageOfferingsQuery } from '@r-client/republic/data/offerings';
import { QueryError } from '@r-client/republic/feature/errors';
import { OfferingDealCard } from '@r-client/republic/feature/offering-deal-card';
import { ASSET_TYPES } from '@r-client/shared/data/analytics';
import { isInitialQueryLoading } from '@r-client/shared/util/core';

import { useInvestmentOpportunitiesAnalytics } from '../../../hooks/use-investment-opportunities-analytics';
import { buildGrowthStageSearchParams } from '../../../utils';
import {
  EMPTY_MESSAGE,
  GRID_BLEED,
  GRID_COLUMNS,
  GRID_SPACING_X,
  GRID_SPACING_Y,
  OFFERINGS_PAGE_SIZE,
} from './constants';
import { OfferingsGridSkeleton } from './offerings-grid-skeleton/offerings-grid-skeleton';
import { IOfferingsGridProps } from './types';

/**
 * Loads the growth-stage offerings and renders them as `CardDeal`s.
 *
 * `observer` is required for the same reason as `SectorFilter`: `BaseQueryModel`
 * only installs its variables autorun once it is MobX-observed, so without this
 * wrapper a sector or sort change would silently never refetch.
 */
export const OfferingsGrid = observer(function OfferingsGrid({
  sectorSlug,
  sort,
}: IOfferingsGridProps) {
  const offeringsQuery = useGrowthStageOfferingsQuery({
    variables: {
      first: OFFERINGS_PAGE_SIZE,
      searchParams: buildGrowthStageSearchParams({ sectorSlug, sort }),
    },
    notifyOnNetworkStatusChange: true,
  });
  const { assetCardClicked, assetFollowed, assetUnfollowed } =
    useInvestmentOpportunitiesAnalytics();

  if (isInitialQueryLoading(offeringsQuery)) {
    return <OfferingsGridSkeleton />;
  }

  if (offeringsQuery.isRejected) {
    return <QueryError queries={[offeringsQuery]} showRefetch />;
  }

  const offerings = offeringsQuery.data?.offeringsSearch.nodes ?? [];

  if (!offerings.length) {
    return (
      <Text
        size="body-md"
        variant="text-secondary"
        data-testid="growth-stage-offerings-empty"
      >
        {EMPTY_MESSAGE}
      </Text>
    );
  }

  return (
    <SimpleGrid
      mx={GRID_BLEED}
      columns={GRID_COLUMNS}
      spacingX={GRID_SPACING_X}
      spacingY={GRID_SPACING_Y}
      data-testid="growth-stage-offerings-grid"
    >
      {offerings.map((offering) => (
        <OfferingDealCard
          key={offering.id}
          offering={offering}
          onCardClick={() =>
            assetCardClicked(
              ASSET_TYPES.OFFERING,
              offering.slug,
              sectorSlug || 'all',
              sort
            )
          }
          onFollow={() =>
            assetFollowed(
              ASSET_TYPES.OFFERING,
              offering.slug,
              sectorSlug || 'all',
              sort
            )
          }
          onUnfollow={() =>
            assetUnfollowed(
              ASSET_TYPES.OFFERING,
              offering.slug,
              sectorSlug || 'all',
              sort
            )
          }
        />
      ))}
    </SimpleGrid>
  );
});
